import React, { useState, useEffect, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'

import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppReviewDraft,
    clearAppReviewDraft,
    createAppReviewDraftPayload,
    createEditAppReviewDraftKey,
    readAppReviewDraft,
    saveAppReviewDraft,
} from './appReviewDraftUtils'

const PANEL_SX = FORM_PANEL_SX

const getChoiceCardSx = (selected, disabled = false) => ({
    ...getFormChoiceCardSx(selected, disabled),
    p: 1.5,
    width: '100%',
})

const REVIEW_STATUS_OPTIONS = [
    {
        value: 0,
        title: '신청중',
        description: '아직 승인 여부를 확정하지 않은 상태입니다.',
    },
    {
        value: 1,
        title: '승인',
        description: '리뷰를 승인하고 사용자 화면에 반영합니다.',
    },
    {
        value: 2,
        title: '반려',
        description: '리뷰를 반려 상태로 저장합니다.',
    },
]

function FormSection({ title, description, children }) {
    return (
        <Paper variant="outlined" sx={PANEL_SX}>
            <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

// 카드형 선택 UI에서도 기존 입력 변경 핸들러가 받던 name/value 이벤트 형태를 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function ReviewStatusChoiceGroup({ value, disabled, onChange }) {
    return (
        <FormControl fullWidth>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                승인여부
            </Typography>
            <Box
                role="radiogroup"
                aria-label="승인여부"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                    gap: 1,
                    mt: 1,
                }}
            >
                {REVIEW_STATUS_OPTIONS.map((option) => {
                    const selected = Number(value ?? 0) === option.value

                    return (
                        <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={(
                                <Radio
                                    name="status"
                                    checked={selected}
                                    onChange={() => onChange(createFieldChangeEvent('status', String(option.value)))}
                                    disabled={disabled}
                                />
                            )}
                            disabled={disabled}
                            sx={getChoiceCardSx(selected, disabled)}
                            label={(
                                <ChoiceCardLabel
                                    title={option.title}
                                    description={option.description}
                                />
                            )}
                        />
                    )
                })}
            </Box>
        </FormControl>
    )
}

function ReviewImagePreview({ imageSrc }) {
    return (
        <FormSection
            title="리뷰 이미지"
            description="사용자가 등록한 운동시설 리뷰 이미지를 확인합니다."
        >
            <Box
                sx={{
                    ...FORM_IMAGE_PREVIEW_FRAME_SX,
                    minHeight: 280,
                    height: 'auto',
                    maxHeight: 520,
                    overflow: 'auto',
                }}
            >
                {imageSrc ? (
                    <Box
                        component="img"
                        src={imageSrc}
                        alt="리뷰 이미지"
                        sx={{
                            width: '100%',
                            maxHeight: 520,
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        등록된 이미지가 없습니다.
                    </Typography>
                )}
            </Box>
        </FormSection>
    )
}

export default function AppReview(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.sportsReviewId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchStatus: 99, searchPopupStatus: '' }
    const listSearch = location.search || ''
    const draftKey = createEditAppReviewDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState({})
    const [image, setImage] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const initialDraftSnapshotRef = useRef('')

    useEffect(() => {
        const getData = async () => {
            if (!id) {
                const errorMessage = '필수 파라미터가 없어 App 운동시설 리뷰 상세를 불러올 수 없습니다.'
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
                setLoading(false)
                return
            }

            setLoading(true)
            setDraftReady(false)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/appSportsReview/getDetail', { sportsReviewId: id })

                if (data.code === 200) {
                    const baseParams = data.data || {}
                    const storedDraft = readAppReviewDraft(draftKey)
                    const restoredDraft = applyAppReviewDraft({
                        baseParams,
                        draft: storedDraft,
                    })

                    initialDraftSnapshotRef.current = JSON.stringify(createAppReviewDraftPayload({
                        params: baseParams,
                    }))

                    setParams(restoredDraft.params)
                    setImage(baseParams.image === null ? '' : `${process.env.REACT_APP_AWS_S3_URL}/${baseParams.image}`)
                    setDetailErrorMessage(baseParams?.sportsReviewId ? '' : 'App 운동시설 리뷰 상세 정보를 찾을 수 없습니다.')
                    setDraftReady(true)
                } else if (data.code === 403) {
                    setLoading(false)
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || 'App 운동시설 리뷰 상세를 불러오지 못했습니다.'
                    setParams({})
                    setImage('')
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = 'App 운동시설 리뷰 상세를 불러오지 못했습니다.'
                setParams({})
                setImage('')
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }

            setLoading(false)
        }
        getData()
    }, [draftKey, history, id])

    useEffect(() => {
        if (!draftReady || !params?.sportsReviewId) {
            return
        }

        // App 운동시설 리뷰 상세는 목록 query를 건드리지 않고 승인상태 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppReviewDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppReviewDraft(draftKey)
            return
        }

        saveAppReviewDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="App 운동시설 리뷰 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />
            )}
            sx={{ mt: 2 }}
        />
    )

    const moveToList = (snackbarState) => {
        // 저장 후에도 운영자가 보던 App 운동시설 리뷰 목록 조건으로 그대로 복귀시킨다.
        clearAppReviewDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: '/appReviews',
            search: listSearch,
            curPage: curPage,
            searchText: searchText,
            searchSelect: searchSelect,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {})
        }))
    }

    const handleUpdateField = (e) => {
        setParams({
            ...params,
            [e.target.name]: e.target.value
        })
    }

    const updateData = async () => {
        if (saving) {
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appSportsReview/update', { sportsReviewId: id, status: params.status })

            if (data.code === 200) {
                moveToList({ severity: 'success', message: 'App 운동시설 리뷰 상태를 저장했습니다.' })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 운동시설 리뷰 상태를 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        updateData()
    }

    if (loading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (!params?.sportsReviewId) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>App 운동시설 리뷰 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>App 운동시설 리뷰 수정</Title>
                <Box component="form" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12}>
                            <ReviewImagePreview imageSrc={image} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormSection
                                title="승인 상태"
                                description="리뷰 이미지와 내용을 확인한 뒤 노출 상태를 결정합니다."
                            >
                                <ReviewStatusChoiceGroup
                                    value={params.status}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                />
                            </FormSection>
                        </Grid>
                    </Grid>
                    <BottomButtonGroup
                        visible={true}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        backDisabled={saving}
                        onBackClick={() => moveToList()}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
